import { useEffect, useState } from 'react';

import { useSearchImplantAccountsQuery } from '@/app/services/inClinic';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface OwnerSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (ownerId: string, ownerCode: string) => void;
}

export function OwnerSearchDialog({
  open,
  onOpenChange,
  onSelect,
}: OwnerSearchDialogProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pending, setPending] = useState<{ id: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
      setPending(null);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching } = useSearchImplantAccountsQuery(
    debounced,
    {
      skip: debounced.length < 2,
    },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {pending ? 'Confirm Owner Change' : 'Search Embryo Owner'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          {!pending && (
            <>
              <Input
                autoFocus
                placeholder="Account number..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="max-h-64 overflow-y-auto rounded border">
                {debounced.length < 2 && (
                  <div className="text-muted-foreground p-3 text-xs">
                    Type at least 2 characters to search
                  </div>
                )}
                {debounced.length >= 2 && isFetching && (
                  <div className="text-muted-foreground p-3 text-xs">
                    Searching...
                  </div>
                )}
                {debounced.length >= 2 &&
                  !isFetching &&
                  results.length === 0 && (
                    <div className="text-muted-foreground p-3 text-xs">
                      No accounts found
                    </div>
                  )}
                {results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setPending(r)}
                    className="hover:bg-muted/50 block w-full border-b px-3 py-2 text-left text-sm last:border-b-0"
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {pending && (
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                Change embryo owner to{' '}
                <span className="font-semibold">{pending.name}</span>?
              </p>
              <p className="text-muted-foreground text-xs">
                This updates the embryo's primary owner in OvaDrive.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {pending ? (
            <>
              <Button variant="outline" onClick={() => setPending(null)}>
                Back
              </Button>
              <Button
                onClick={() => {
                  onSelect(pending.id, pending.name);
                  onOpenChange(false);
                }}
              >
                Confirm
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
